// src/store/apis/taxDriveSyncApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';

const unwrap = <T>(res: unknown): T =>
  res && typeof res === 'object' && 'data' in res
    ? ((res as { data: unknown }).data as T)
    : (res as T);

export type DriveSyncState = 'NOT_LINKED' | 'IDLE' | 'QUEUED' | 'RUNNING';

export type HeldBackReason =
  | 'MISSING_TYPE'
  | 'MISSING_YEAR'
  | 'UNLINKED_PROPERTY'
  | 'NO_CHECKSUM'
  | 'NO_DESTINATION';

export interface DriveSyncStatus {
  state: DriveSyncState;
  /**
   * Records the next run would act on. Computed from status alone, so it
   * over-counts documents a run reconciles for free — a nudge, not a promise.
   * Never phrase it as "N files will be copied".
   */
  pending: number;
  /** Labelled incompletely; a run will not fix these. Counted separately. */
  heldBack: number;
  progress?: { done: number; total: number };
  jobId?: string | null;
  startedAt?: string | null;
  lastReportAt?: string | null;
  lastReportHasIssues?: boolean;
}

export interface DriveSyncReport {
  startedAt: string;
  finishedAt: string;
  jobId: string | null;
  foldersCreated: Array<{ name: string; parentPath: string }>;
  synced: Array<{ recordId: string; name: string; destPath: string }>;
  duplicates: Array<{
    recordId: string;
    name: string;
    matchedFileId: string;
    path: string;
  }>;
  heldBack: Array<{ recordId: string; name: string; reason: HeldBackReason }>;
  failed: Array<{ recordId: string; name: string; error: string }>;
  duplicateFolders: Array<{
    slot: string;
    path: string;
    candidates: Array<{
      folderId: string;
      name: string;
      fileCount: number;
      lastModifiedBy?: string;
      winner: boolean;
    }>;
  }>;
  unrecognised: Array<{
    folderId: string;
    name: string;
    path: string;
    /**
     * A property this folder plausibly names, when the strict matcher refused
     * it but the two are close — an accountant's folder is often more specific
     * than the address we hold. Shown for confirmation; the sync never acts on
     * it by itself.
     */
    suggestedMatch?: {
      propertyKey: string;
      displayName: string;
      score: number;
    };
  }>;
}

export interface DriveSyncEnqueueResult {
  jobId: string;
  state: DriveSyncState;
  alreadyRunning: boolean;
}

export type LinkedDriveWarning =
  | 'NOT_A_SHARED_DRIVE'
  | 'NO_CONTENT_MANAGER'
  | 'LARGE_DRIVE';

export interface LinkedDrivePreview {
  folderId: string;
  folderName: string;
  sharedDriveId: string | null;
  indexStrategy: 'FLAT' | 'BFS';
  warnings: LinkedDriveWarning[];
  categoryFolders: Array<{
    slot: string;
    folderName: string;
    matched: boolean;
  }>;
  years: Array<{
    financialYearEnd: number;
    folderName: string;
    propertyCount: number;
  }>;
  duplicateSlots: Array<{ slot: string; candidateNames: string[] }>;
  unrecognisedCount: number;
  /** Documents this link would re-file on the next sync. */
  documentsAffected: number;
}

export const taxDriveSyncApi = createApi({
  reducerPath: 'taxDriveSyncApi',
  baseQuery: customBaseQuery,
  tagTypes: ['DriveSyncStatus', 'DriveSyncReport'],
  endpoints: (builder) => ({
    getDriveSyncStatus: builder.query<DriveSyncStatus, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tax/deals/${id}/drive-sync/status`,
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<DriveSyncStatus>(res),
      providesTags: (_r, _e, arg) => [{ type: 'DriveSyncStatus', id: arg.id }],
    }),

    getDriveSyncReport: builder.query<DriveSyncReport | null, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tax/deals/${id}/drive-sync/report`,
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<DriveSyncReport | null>(res),
      providesTags: (_r, _e, arg) => [{ type: 'DriveSyncReport', id: arg.id }],
    }),

    startDriveSync: builder.mutation<DriveSyncEnqueueResult, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tax/deals/${id}/drive-sync`,
        method: 'POST',
      }),
      transformResponse: (res) => unwrap<DriveSyncEnqueueResult>(res),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'DriveSyncStatus', id: arg.id },
      ],
      /**
       * The job is enqueued before any worker picks it up, so the very next
       * poll can still legitimately answer IDLE. Without this patch the button
       * falls back to "Sync to Drive" for one cycle and reads as though the
       * click did nothing.
       *
       * This covers the job LIFECYCLE only. Deal and document caches go stale
       * when the run FINISHES — a different moment, handled in
       * useTaxDealDriveSync.
       */
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          taxDriveSyncApi.util.updateQueryData(
            'getDriveSyncStatus',
            { id },
            (draft) => {
              draft.state = 'QUEUED';
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            taxDriveSyncApi.util.updateQueryData(
              'getDriveSyncStatus',
              { id },
              (draft) => {
                draft.state = data.state;
                draft.jobId = data.jobId;
              },
            ),
          );
        } catch {
          patch.undo();
        }
      },
    }),

    /** Validate a folder and preview what would be adopted, without saving. */
    previewDriveFolder: builder.mutation<
      LinkedDrivePreview | null,
      { id: string; driveFolderId: string }
    >({
      query: ({ id, driveFolderId }) => ({
        url: `/v1/tax/deals/${id}/drive-folder/preview`,
        method: 'POST',
        body: { driveFolderId },
      }),
      transformResponse: (res) => unwrap<LinkedDrivePreview | null>(res),
    }),
  }),
});

export const {
  useGetDriveSyncStatusQuery,
  useGetDriveSyncReportQuery,
  useLazyGetDriveSyncReportQuery,
  useStartDriveSyncMutation,
  usePreviewDriveFolderMutation,
} = taxDriveSyncApi;
